import { ref } from "vue";

import { useProductsStore } from "@/stores/Products";
import { useNotificationAcknowledgement } from "@/composables/notification-acknowledgement";
import { adoptUnscopedValue, ownerScopedKey, storageOwner } from "@/utils/storage-owner";

import { webinarStartTime } from "@tmi/utils/webinar-phase";
import type { Category, ContentfulItem, Product, Webinar } from "@tmi/types/entities";

/** The key's base. Each member's state lives under `base:<owner>` (UX roadmap #63, utils/storage-owner.ts). */
export const CONTENT_READ_STATE_STORAGE_KEY = "tmi-content-read-state-v1";
const DayMs = 24 * 60 * 60 * 1000;

/** How long after publication an item counts as new. */
export const UNREAD_WINDOW_MS = 5 * DayMs;
/**
 * A publish time this far ahead of the device's clock still counts as published now: the CMS and the member's clock
 * disagree by seconds to minutes, and an alert stamped a minute "in the future" used to be left out of every counter
 * until the device caught up (gap_alert_delivery_and_notifications-10).
 */
export const CLOCK_SKEW_TOLERANCE_MS = 5 * 60 * 1000;
/**
 * An upcoming webinar stays new until it starts, not just for 5 days after it was announced, as long as it was
 * announced within this long. The cap keeps a session announced months ahead from sitting in the bell for months.
 */
export const UPCOMING_WEBINAR_WINDOW_MS = 21 * DayMs;
/**
 * Read marks are kept this long after they were made. Longer than any item stays new (an upcoming webinar can be new for
 * UPCOMING_WEBINAR_WINDOW_MS), or an item read early would come back as unread while it is still eligible.
 */
export const READ_STATE_RETENTION_MS = 30 * DayMs;

type ReadState = Record< string, number >;
export type UnreadCountOptions = {
    categorySlug?: string,
    productSlug?: string,
};

const readStateVersion = ref( 0 );

// The parsed state for readStateVersion. Every counter, pill and bell row asked isContentRead, and each call re-read and
// JSON.parsed localStorage (gap_alert_delivery_and_notifications-8); now it is parsed once per change.
// The state is also per member (UX roadmap #63), so a cached parse belongs to the owner it was read for.
let parsedState: ReadState | null = null;
let parsedVersion = -1;
let parsedOwner: string | null | undefined = undefined;

function canUseLocalStorage(): boolean {
    return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

/**
 * The signed-in member's key. Reading `storageOwner` makes every counter re-run when another member signs in, and the
 * first read for a member hands them the state this browser kept before it was per member.
 */
function storageKey(): string {
    const owner: string | null = storageOwner.value;
    adoptUnscopedValue( CONTENT_READ_STATE_STORAGE_KEY, owner );
    return ownerScopedKey( CONTENT_READ_STATE_STORAGE_KEY, owner );
}

// UX roadmap #63: a content opened in another tab clears its badges here too. Before, only this tab's writes did.
if ( typeof window !== "undefined" ) {
    window.addEventListener( "storage", ( event: StorageEvent ) => {
        if ( null === event.key || ownerScopedKey( CONTENT_READ_STATE_STORAGE_KEY, storageOwner.value ) === event.key ) {
            readStateVersion.value++;
        }
    } );
}

function readState(): ReadState {
    if ( !canUseLocalStorage() ) {
        return {};
    }

    try {
        const key: string = storageKey();
        const raw = window.localStorage.getItem( key );
        if ( !raw ) {
            return {};
        }

        const parsed = JSON.parse( raw ) as ReadState;
        const pruned = pruneState( parsed );
        if ( Object.keys( pruned ).length !== Object.keys( parsed ).length ) {
            window.localStorage.setItem( key, JSON.stringify( pruned ) );
        }

        return pruned;
    } catch {
        return {};
    }
}

/**
 * The read state, parsed once per readStateVersion and storage owner. Tracks both, so callers in a computed follow
 * changes and re-run when another member signs in.
 */
function currentState(): ReadState {
    const version = readStateVersion.value;
    const owner: string | null = storageOwner.value;
    if ( parsedState === null || parsedVersion !== version || parsedOwner !== owner ) {
        parsedState = readState();
        parsedVersion = version;
        parsedOwner = owner;
    }

    return parsedState;
}

function writeState( state: ReadState ): void {
    if ( !canUseLocalStorage() ) {
        return;
    }

    try {
        window.localStorage.setItem( storageKey(), JSON.stringify( state ) );
    } catch {
        // Ignore storage failures; unread badges are a progressive UI enhancement.
    }

    // This tab's state moves on even when the write failed (private mode, quota), so an opened item stops counting.
    parsedState = state;
    parsedVersion = ++readStateVersion.value;
    parsedOwner = storageOwner.value;
}

function pruneState( state: ReadState, now = Date.now() ): ReadState {
    const next: ReadState = {};
    Object.entries( state ).forEach( ( [ key, viewedAt ] ) => {
        if ( now - viewedAt <= READ_STATE_RETENTION_MS ) {
            next[ key ] = viewedAt;
        }
    } );

    return next;
}

function canonicalContentType( contentType: string | undefined ): string | undefined {
    switch ( contentType ) {
        case 'article':
            return 'articles';
        case 'webinar':
            return 'webinars';
        default:
            return contentType;
    }
}

function legacyContentTypes( contentType: string | undefined ): string[] {
    switch ( contentType ) {
        case 'articles':
            return [ 'article' ];
        case 'webinars':
            return [ 'webinar' ];
        default:
            return [];
    }
}

function contentKey( content: ContentfulItem | undefined, contentType: string | undefined ): string | undefined {
    const id = content?.contentfulId ?? content?.slug;
    const normalizedType = canonicalContentType( contentType );
    if ( !id || !normalizedType ) {
        return undefined;
    }

    return `${ normalizedType }:${ id }`;
}

function readableContentKeys( content: ContentfulItem | undefined, contentType: string | undefined ): string[] {
    const id = content?.contentfulId ?? content?.slug;
    const primaryKey = contentKey( content, contentType );
    if ( !id || !primaryKey ) {
        return [];
    }

    return [
        primaryKey,
        ...legacyContentTypes( canonicalContentType( contentType ) ).map( ( legacyType: string ) => `${ legacyType }:${ id }` )
    ];
}

function timestampOf( value: string | Date | undefined ): number {
    return value ? new Date( value ).getTime() : Number.NaN;
}

/**
 * Whether an item is recent enough to be new: published within UNREAD_WINDOW_MS (allowing CLOCK_SKEW_TOLERANCE_MS of
 * clock disagreement), or an upcoming webinar that has not started yet. The bell, every unread counter and the NEW
 * pill share this one rule, so they agree on what is new.
 */
export function isNotifiableRecent( content: ContentfulItem | undefined, now = Date.now() ): boolean {
    const publishedAt = timestampOf( content?.publishedDate );
    if ( !Number.isFinite( publishedAt ) || publishedAt - now > CLOCK_SKEW_TOLERANCE_MS ) {
        return false;
    }

    if ( now - publishedAt <= UNREAD_WINDOW_MS ) {
        return true;
    }

    // The bell keyed on publishedDate alone, so a webinar announced a week ahead dropped out before it happened. The start
    // is read by the webinar rule (webinarStartTime: the service's digits are New York time, its 'Z' names no zone);
    // new Date() read the 'Z' as UTC and ended the wait four hours early (five in winter).
    const startsAt: number = webinarStartTime( ( content as Webinar | undefined )?.webinarDate )?.toMillis() ?? Number.NaN;
    return Number.isFinite( startsAt )
        && startsAt >= now
        && now - publishedAt <= UPCOMING_WEBINAR_WINDOW_MS;
}

/** The items in `contents` that are new by isNotifiableRecent, newest first. One pass, one date parse per item. */
export function recentNotifiableContent< T extends ContentfulItem >( contents: readonly T[], now = Date.now() ): T[] {
    return contents
        .filter( ( content: T ) => isNotifiableRecent( content, now ) )
        .sort( ( a: T, b: T ) => timestampOf( b.publishedDate ) - timestampOf( a.publishedDate ) );
}

function contentMatchesProduct( content: ContentfulItem, productSlug?: string ): boolean {
    const products = ( content.products ?? [] ) as Product[];
    if ( !productSlug ) {
        return true;
    }

    const activeProduct = useProductsStore().productBySlug( productSlug );
    return products.some( ( product: Product ) =>
        product.slug === productSlug || ( !!activeProduct && product.id === activeProduct.id )
    );
}

function contentMatchesCategory( content: ContentfulItem, categorySlug?: string ): boolean {
    if ( !categorySlug ) {
        return true;
    }

    const categories = ( content.categories ?? [] ) as Category[];
    return categories.some( ( category: Category ) => category.slug === categorySlug );
}

function normalizeUnreadCountOptions( productSlugOrOptions?: string | UnreadCountOptions ): UnreadCountOptions {
    if ( typeof productSlugOrOptions === 'string' ) {
        return { productSlug: productSlugOrOptions };
    }

    return productSlugOrOptions ?? {};
}

export type UnreadContentEntry = {
    content: ContentfulItem,
    contentType: string,
};

export function useContentReadState() {
    // Acknowledgement (the item was listed when the member opened the bell) is
    // treated as "cleared" by every counter sourced from this composable —
    // sidebar badges, ProductWorkspace tabs, top notifications bell. The
    // dropdown LIST (unreadRecentContentList) and the NEW pill
    // (isUnreadRecentContent) intentionally do NOT factor it in: both stay
    // until the member actually opens the content.
    const { isAcknowledged } = useNotificationAcknowledgement();

    function markContentRead( content: ContentfulItem | undefined, contentType: string | undefined ): void {
        const key = contentKey( content, contentType );
        if ( !key ) {
            return;
        }

        const state = pruneState( { ...currentState() } );
        state[ key ] = Date.now();
        writeState( state );
    }

    function isContentRead( content: ContentfulItem | undefined, contentType: string | undefined ): boolean {
        const keys = readableContentKeys( content, contentType );
        if ( keys.length === 0 ) {
            return false;
        }

        const state = currentState();
        return keys.some( ( key: string ) => !!state[ key ] );
    }

    function isUnreadRecentContent( content: ContentfulItem | undefined, contentType: string | undefined ): boolean {
        return !!content
            && isNotifiableRecent( content )
            && !isContentRead( content, contentType );
    }

    function unreadCountForContent( contents: ContentfulItem[], contentType: string, productSlugOrOptions?: string | UnreadCountOptions ): number {
        const options = normalizeUnreadCountOptions( productSlugOrOptions );

        return contents.reduce( ( count: number, content: ContentfulItem ) => {
            if (
                !isNotifiableRecent( content ) ||
                isContentRead( content, contentType ) ||
                isAcknowledged( content, contentType ) ||
                !contentMatchesProduct( content, options.productSlug ) ||
                !contentMatchesCategory( content, options.categorySlug )
            ) {
                return count;
            }

            return count + 1;
        }, 0 );
    }

    function unreadRecentContentList( contents: ContentfulItem[], contentType: string, productSlugOrOptions?: string | UnreadCountOptions ): ContentfulItem[] {
        const options = normalizeUnreadCountOptions( productSlugOrOptions );

        return recentNotifiableContent( contents ).filter( ( content: ContentfulItem ) =>
            !isContentRead( content, contentType ) &&
            contentMatchesProduct( content, options.productSlug ) &&
            contentMatchesCategory( content, options.categorySlug )
        );
    }

    /** The bell's 'Earlier' rows: items still recent that the member has already opened, newest first. */
    function readRecentContentList( contents: ContentfulItem[], contentType: string ): ContentfulItem[] {
        return recentNotifiableContent( contents ).filter( ( content: ContentfulItem ) => isContentRead( content, contentType ) );
    }

    function markAllContentRead( contents: ContentfulItem[], contentType: string, productSlugOrOptions?: string | UnreadCountOptions ): void {
        const options = normalizeUnreadCountOptions( productSlugOrOptions );
        const state = pruneState( { ...currentState() } );
        const now = Date.now();
        let changed = false;

        contents.forEach( ( content: ContentfulItem ) => {
            if (
                !isNotifiableRecent( content, now ) ||
                !contentMatchesProduct( content, options.productSlug ) ||
                !contentMatchesCategory( content, options.categorySlug )
            ) {
                return;
            }

            readableContentKeys( content, contentType ).forEach( ( key: string ) => {
                state[ key ] = now;
                changed = true;
            } );
        } );

        if ( changed ) {
            writeState( state );
        }
    }

    return {
        isUnreadRecentContent,
        markAllContentRead,
        markContentRead,
        readRecentContentList,
        unreadCountForContent,
        unreadRecentContentList,
    };
}
